/**
 * Keyboard Shortcut Service
 *
 * Core service for managing keyboard shortcuts following Google SPA patterns.
 * Handles registration, event listening, platform detection, and accessibility.
 *
 * @remarks
 * This service implements:
 * - Gmail-style single letter shortcuts (j/k navigation)
 * - Google Docs-style save patterns (ctrl+s/cmd+s)
 * - Drive-style help (?)
 * - WCAG 2.2 keyboard accessibility guidelines
 * - ARIA aria-keyshortcuts attribute support
 * - Input context awareness (disable single letters in inputs)
 *
 * @example Basic usage
 * ```typescript
 * const service = KeyboardShortcutService.getInstance();
 *
 * service.register('ctrl+s', handleSave, {
 *   description: 'Save document',
 *   category: 'Actions'
 * });
 * ```
 *
 * @example Gmail-style navigation
 * ```typescript
 * service.register('j', handleNext, {
 *   description: 'Next item',
 *   category: 'Navigation',
 *   requireNotInput: true
 * });
 * ```
 *
 * @example Go-to sequences (Gmail g+i pattern)
 * ```typescript
 * service.register('g+i', () => navigate('/inbox'), {
 *   description: 'Go to inbox',
 *   category: 'Navigation'
 * });
 * ```
 */

import type {
  ShortcutPattern,
  ShortcutOptions,
  RegisteredShortcut,
  ShortcutGroup,
  KeyboardEventMeta,
  ShortcutTier
} from './types';

export class KeyboardShortcutService {
  private static instance: KeyboardShortcutService;

  private shortcuts: Map<string, RegisteredShortcut> = new Map();
  private sequenceBuffer: string[] = [];
  private sequenceTimeout: NodeJS.Timeout | null = null;
  private sequenceTimeoutMs = 1000;
  private nextId = 0;
  private enabled = true;

  private constructor() {
    if (typeof window !== 'undefined') {
      this.attachListener();
    }
  }

  /**
   * Get singleton instance
   */
  public static getInstance(): KeyboardShortcutService {
    if (!KeyboardShortcutService.instance) {
      KeyboardShortcutService.instance = new KeyboardShortcutService();
    }
    return KeyboardShortcutService.instance;
  }

  /**
   * Register a keyboard shortcut
   *
   * @param pattern - Shortcut pattern (e.g., 'ctrl+s', 'j', 'g+i')
   * @param callback - Function to execute when shortcut is triggered
   * @param options - Configuration options
   * @returns Unique ID for this shortcut (use with unregister)
   *
   * @example Save shortcut (Google Docs style)
   * ```typescript
   * register('ctrl+s', handleSave, {
   *   description: 'Save document',
   *   category: 'Actions',
   *   preventDefault: true
   * });
   * ```
   *
   * @example Navigation (Gmail style)
   * ```typescript
   * register('j', selectNext, {
   *   description: 'Next item',
   *   category: 'Navigation',
   *   requireNotInput: true
   * });
   * ```
   *
   * @example Sequence shortcut (Gmail g+i pattern)
   * ```typescript
   * register('g+i', () => navigate('/inbox'), {
   *   description: 'Go to inbox',
   *   category: 'Navigation'
   * });
   * ```
   */
  public register(
    pattern: ShortcutPattern,
    callback: (event: KeyboardEvent) => void,
    options: ShortcutOptions = {}
  ): string {
    const id = `shortcut-${this.nextId++}`;

    const normalizedPattern = this.normalizePattern(pattern);

    const mergedOptions = {
      description: options.description || '',
      category: options.category || 'General',
      tier: options.tier || 'frequent',
      ariaLabel: options.ariaLabel || '',
      scope: options.scope || 'local',
      requireNotInput: options.requireNotInput !== undefined ? options.requireNotInput : this.isSingleLetterShortcut(normalizedPattern),
      requireFocus: options.requireFocus,
      enabled: options.enabled !== undefined ? options.enabled : true,
      preventDefault: options.preventDefault !== undefined ? options.preventDefault : true,
      stopPropagation: options.stopPropagation !== undefined ? options.stopPropagation : true,
    } as Required<ShortcutOptions>;

    const registered: RegisteredShortcut = {
      pattern: normalizedPattern,
      callback,
      options: mergedOptions,
      id,
    };

    this.shortcuts.set(id, registered);

    return id;
  }

  /**
   * Unregister a shortcut
   *
   * @param id - ID returned from register()
   *
   * @example
   * ```typescript
   * const id = service.register('j', handleNext);
   * // Later...
   * service.unregister(id);
   * ```
   */
  public unregister(id: string): void {
    this.shortcuts.delete(id);
  }

  /**
   * Unregister all shortcuts matching a pattern
   *
   * @param pattern - Shortcut pattern
   *
   * @example
   * ```typescript
   * service.unregisterPattern('ctrl+s');
   * ```
   */
  public unregisterPattern(pattern: ShortcutPattern): void {
    const normalized = this.normalizePattern(pattern);

    for (const [id, shortcut] of this.shortcuts.entries()) {
      if (shortcut.pattern === normalized) {
        this.shortcuts.delete(id);
      }
    }
  }

  /**
   * Enable/disable all shortcuts
   *
   * @param enabled - Whether shortcuts are enabled
   *
   * @example
   * ```typescript
   * service.setEnabled(false);
   * service.setEnabled(true);
   * ```
   */
  public setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }

  /**
   * Get all registered shortcuts grouped by category
   *
   * @returns Array of shortcut groups for displaying help
   *
   * @example
   * ```typescript
   * const groups = service.getShortcutGroups();
   * groups.forEach(group => {
   *   console.log(group.category);
   *   group.shortcuts.forEach(s => {
   *     console.log(`  ${s.pattern}: ${s.description}`);
   *   });
   * });
   * ```
   */
  public getShortcutGroups(tier?: ShortcutTier): ShortcutGroup[] {
    const groups = new Map<string, ShortcutGroup>();

    for (const shortcut of this.shortcuts.values()) {
      if (tier && shortcut.options.tier !== tier) {
        continue;
      }

      const { category } = shortcut.options;

      if (!groups.has(category)) {
        groups.set(category, {
          category,
          shortcuts: [],
        });
      }

      groups.get(category)!.shortcuts.push({
        pattern: shortcut.pattern,
        description: shortcut.options.description,
        tier: shortcut.options.tier,
      });
    }

    return Array.from(groups.values());
  }

  /**
   * Get ARIA keyshortcuts attribute value for an element
   *
   * @param patterns - Shortcut patterns applicable to the element
   * @returns Space-separated string for aria-keyshortcuts
   *
   * @example
   * ```typescript
   * <button aria-keyshortcuts={service.getAriaKeyshortcuts(['ctrl+s', 'cmd+s'])}>
   *   Save
   * </button>
   * ```
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Attributes/aria-keyshortcuts
   */
  public getAriaKeyshortcuts(patterns: ShortcutPattern[]): string {
    return patterns
      .map(p => this.normalizePattern(p))
      .map(p => this.convertToAriaFormat(p))
      .join(' ');
  }

  private attachListener(): void {
    window.addEventListener('keydown', this.handleKeyDown.bind(this), false);
  }

  private handleKeyDown(event: KeyboardEvent): void {
    if (!event.key) {
      return;
    }

    if (!this.enabled) {
      return;
    }

    const modifierKeys = ['Control', 'Shift', 'Alt', 'Meta', 'Command'];
    if (modifierKeys.includes(event.key)) {
      return;
    }

    const meta = this.extractEventMeta(event);

    if (this.isSequenceKey(meta.key)) {
      this.handleSequenceKey(event, meta);
      return;
    }

    const pattern = this.buildPatternFromEvent(event, meta);

    const hasRegisteredShortcut = Array.from(this.shortcuts.values()).some(
      shortcut => shortcut.pattern === pattern
    );

    if (!hasRegisteredShortcut) {
      return;
    }

    this.tryExecuteShortcut(pattern, event, meta);
  }

  private handleSequenceKey(event: KeyboardEvent, meta: KeyboardEventMeta): void {
    this.sequenceBuffer.push(meta.key.toLowerCase());

    if (this.sequenceTimeout) {
      clearTimeout(this.sequenceTimeout);
    }

    this.sequenceTimeout = setTimeout(() => {
      this.sequenceBuffer = [];
      this.sequenceTimeout = null;
    }, this.sequenceTimeoutMs);

    const sequencePattern = this.sequenceBuffer.join('+');

    this.tryExecuteShortcut(sequencePattern, event, meta);
  }

  private tryExecuteShortcut(
    pattern: string,
    event: KeyboardEvent,
    meta: KeyboardEventMeta
  ): void {
    for (const shortcut of this.shortcuts.values()) {
      if (shortcut.pattern !== pattern) {
        continue;
      }

      if (!this.shouldExecute(shortcut, meta)) {
        continue;
      }

      if (shortcut.options.preventDefault) {
        event.preventDefault();
      }

      if (shortcut.options.stopPropagation) {
        event.stopPropagation();
      }

      shortcut.callback(event);

      this.sequenceBuffer = [];
      if (this.sequenceTimeout) {
        clearTimeout(this.sequenceTimeout);
        this.sequenceTimeout = null;
      }

      break;
    }
  }

  private shouldExecute(shortcut: RegisteredShortcut, meta: KeyboardEventMeta): boolean {
    const { enabled, requireNotInput, requireFocus } = shortcut.options;

    const isEnabled = typeof enabled === 'function' ? enabled() : enabled;
    if (!isEnabled) {
      return false;
    }

    if (requireNotInput && meta.isInput) {
      return false;
    }

    if (requireFocus) {
      const requiredElement = typeof requireFocus === 'string'
        ? document.querySelector(requireFocus)
        : requireFocus;

      if (document.activeElement !== requiredElement) {
        return false;
      }
    }

    return true;
  }

  private extractEventMeta(event: KeyboardEvent): KeyboardEventMeta {
    const target = event.target as HTMLElement;

    const isInput = this.isInputElement(target);

    return {
      key: event.key,
      code: event.code,
      ctrl: event.ctrlKey || event.metaKey,
      shift: event.shiftKey,
      alt: event.altKey,
      meta: event.metaKey,
      isInput,
      target,
    };
  }

  private isInputElement(element: HTMLElement): boolean {
    const tagName = element.tagName.toLowerCase();

    if (['input', 'textarea', 'select'].includes(tagName)) {
      return true;
    }

    if (element.isContentEditable) {
      return true;
    }

    return false;
  }

  private buildPatternFromEvent(event: KeyboardEvent, meta: KeyboardEventMeta): string {
    const parts: string[] = [];

    if (meta.ctrl) parts.push('ctrl');
    if (meta.shift) parts.push('shift');
    if (meta.alt) parts.push('alt');

    const key = this.normalizeKey(event.key);
    parts.push(key);

    return parts.join('+');
  }

  private normalizePattern(pattern: string): string {
    return pattern
      .toLowerCase()
      .replace(/\s+/g, '')
      .replace(/command/g, 'cmd')
      .replace(/cmd/g, 'ctrl')
      .replace(/control/g, 'ctrl')
      .replace(/option/g, 'alt');
  }

  private normalizeKey(key: string): string {
    const normalized = key.toLowerCase();

    const keyMap: Record<string, string> = {
      ' ': 'space',
      'escape': 'esc',
    };

    return keyMap[normalized] || normalized;
  }

  private isSingleLetterShortcut(pattern: string): boolean {
    return pattern.length === 1 && /^[a-z]$/.test(pattern);
  }

  private isSequenceKey(key: string): boolean {
    if (this.sequenceBuffer.length === 0) {
      for (const shortcut of this.shortcuts.values()) {
        if (shortcut.pattern.includes('+') && shortcut.pattern.startsWith(key.toLowerCase())) {
          return true;
        }
      }
    } else {
      return true;
    }

    return false;
  }

  private convertToAriaFormat(pattern: string): string {
    if (!pattern.includes('ctrl') && !pattern.includes('shift') && !pattern.includes('alt')) {
      return pattern.split('+').join(' ').toUpperCase();
    }

    return pattern
      .replace(/ctrl/g, 'Control')
      .replace(/shift/g, 'Shift')
      .replace(/alt/g, 'Alt')
      .split('+')
      .map((part, idx, arr) => idx === arr.length - 1 ? part.toUpperCase() : part)
      .join('+');
  }
}

export const keyboardShortcutService = KeyboardShortcutService.getInstance();
